// Back from a product page, Safari on iPhone often rebuilds the catalogue
// instead of restoring it from the back/forward cache. The stories arrive after
// the page loads and grid pages arrive one by one, so any restore by pixel runs
// on a page that is still short or still changing height: the shopper lands at
// the top or on an unrelated row (YUN-116). Remember which story or card they
// were on and pin that element back to the same place on screen once it exists.
import { getScrollContainer } from '@theme/scroll-container';

const KEY = 'giesto:catalog-position';
const MAX_AGE = 30 * 60 * 1000;
const STORY = '[data-story-groups] > [data-family]';
const CARD = '[ref="cards[]"][data-product-id]';
const catalog = document.querySelector('giesto-store-collection');

if (catalog) {
  // Per history entry: the browser's own restore would race the one below.
  history.scrollRestoration = 'manual';

  let clicked = null;
  let takenOver = false;
  let pinning = null;
  // Story products are copies of grid cards and keep their attributes, so the
  // story itself must win; the grid behind the stories is hidden meanwhile.
  const itemOf = element => element.closest(STORY) || element.closest(CARD);
  const shown = element => element.getClientRects().length > 0;
  document.addEventListener('click', event => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link && catalog.contains(link)) clicked = { element: itemOf(link), time: Date.now() };
    if (!link || !catalog.contains(link) || !matchMedia('(max-width:1024px)').matches || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const productPath = raw => {
      try {
        const url = new URL(raw, location.origin);
        return url.origin === location.origin && /\/products\/[^/]+\/?$/.test(url.pathname) ? url.pathname : '';
      } catch { return ''; }
    };
    const selected = productPath(link.href);
    if (!selected) return;
    const items = [];
    // Read the visible list: includes editorial/color order and active filters,
    // while excluding duplicate hidden grids and unopened overlays.
    for (const anchor of catalog.querySelectorAll('a[href*="/products/"]')) {
      if (!shown(anchor) || anchor.closest('dialog, [hidden]')) continue;
      const url = productPath(anchor.href);
      if (url && !items.some(item => item.url === url)) items.push({url, title:anchor.getAttribute('aria-label') || anchor.textContent.trim() || anchor.querySelector('img')?.alt || ''});
    }
    if (items.some(item => item.url === selected)) {
      try { sessionStorage.setItem('giesto:product-browse', JSON.stringify({time:Date.now(),items})); } catch {}
    }

  }, true);

  const visibleItem = () => {
    const line = innerHeight / 3;
    const items = [...catalog.querySelectorAll(catalog.dataset.gacView === 'stories' ? STORY : CARD)].filter(shown);
    return items.find(item => { const box = item.getBoundingClientRect(); return box.top <= line && box.bottom > line; })
      || items.find(item => item.getBoundingClientRect().bottom > 0);
  };

  addEventListener('pagehide', () => {
    takenOver = false;
    const item = clicked?.element?.isConnected && Date.now() - clicked.time < 10000 ? clicked.element : visibleItem();
    if (!item) return;
    const record = {
      path: location.pathname,
      view: catalog.dataset.gacView || '',
      family: item.matches(STORY) ? item.dataset.family : null,
      product: item.matches(CARD) ? item.dataset.productId : null,
      offset: Math.round(item.getBoundingClientRect().top),
      time: Date.now(),
    };
    try { sessionStorage.setItem(KEY, JSON.stringify(record)); } catch {}
  });

  const find = record => {
    if (record.product) return [...catalog.querySelectorAll(`[ref="cards[]"][data-product-id="${CSS.escape(record.product)}"]`)].find(card => shown(card) && !card.closest(STORY));
    return [...catalog.querySelectorAll(STORY)].find(story => story.dataset.family === record.family && shown(story));
  };

  const restore = () => {
    if (takenOver) return;
    let record;
    try { record = JSON.parse(sessionStorage.getItem(KEY)); } catch {}
    if (!record || record.path !== location.pathname || record.view !== (catalog.dataset.gacView || '') || Date.now() - record.time > MAX_AGE) return;

    // Stories above the target can still finish loading and change height, so
    // keep pinning until the position holds, and stop as soon as the shopper
    // takes over.
    pinning?.abort();
    const controller = new AbortController();
    pinning = controller;
    const stop = () => controller.abort();
    const takeOver = () => { takenOver = true; stop(); };
    for (const type of ['touchstart', 'wheel', 'keydown', 'mousedown']) addEventListener(type, takeOver, { capture: true, passive: true, signal: controller.signal });
    const started = performance.now();
    let steady = 0;
    let earlier = 0;
    const step = () => {
      if (controller.signal.aborted) return;
      const container = getScrollContainer();
      const item = find(record);
      if (item) {
        const target = Math.max(0, Math.round(container.scrollTop + item.getBoundingClientRect().top - record.offset));
        if (Math.abs(target - container.scrollTop) > 2) {
          container.scrollTo({ top: target, behavior: 'instant' });
          steady = 0;
        } else steady++;
      } else if (record.product && earlier < 6) {
        // The grid reopens on the last page it had reached; the card can sit
        // on an earlier one. The sale grid loads those by itself at the top.
        // Until the list is upgraded the link is a plain link, and a click
        // would leave the page.
        const previous = catalog.querySelector('[data-gac-explicit-previous]:not([hidden]):not([aria-busy])');
        if (previous && typeof previous.closest('results-list')?.loadPreviousPage === 'function') { previous.click(); earlier++; }
      }
      if (steady >= 20 || performance.now() - started > 10000) return stop();
      setTimeout(step, 100);
    };
    step();
  };

  const type = performance.getEntriesByType('navigation')[0]?.type;
  const returning = type === 'back_forward' || type === 'reload';
  if (returning) restore();
  // scroll-container.js puts back its raw pixel offset on every pageshow, which
  // on a fresh load comes after the load event. Its listener was registered
  // first, so a frame scheduled here runs after its frame and re-pins the item.
  addEventListener('pageshow', event => {
    if (event.persisted || returning) requestAnimationFrame(restore);
  });
}
